import { KEEP_PLACE } from "@/lib/search";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { CALL_TABS, shiftDay, type CallTab } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { TableCard } from "@/components/app/patterns/table-card";
import { useFacilityToday } from "@/hooks/use-facility-today";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { useCan } from "@/lib/policy";
import { orpc } from "@/lib/orpc";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { CallFilters } from "./-components/call-filters";
import { DAY_WINDOWS } from "./-components/day-windows";
import { CallRowActions } from "./-components/call-row-actions";
import { callColumns, callHasActions } from "./-components/call-columns";
import { SearchField } from "@/components/app/patterns/search-field";

const isTab = (value: unknown): value is CallTab =>
  typeof value === "string" && (CALL_TABS as readonly string[]).includes(value);

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/calls/")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: isTab(search.tab) ? search.tab : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
    /** A user id, or `unassigned`. Absent means anyone. */
    assignee: typeof search.assignee === "string" && search.assignee ? search.assignee : undefined,
    /** One resident's calls. */
    residentId:
      typeof search.residentId === "string" && search.residentId ? search.residentId : undefined,
    /** The name a call was put through to; the transfer records no id. */
    transferredTo:
      typeof search.transferredTo === "string" && search.transferredTo
        ? search.transferredTo
        : undefined,
    /** A window, as two dates. `days` is only how the preset is remembered. */
    days:
      typeof search.days === "number" && DAY_WINDOWS.includes(search.days)
        ? search.days
        : undefined,
    /* A range somebody typed, which the presets cannot express: "the week of
       the inspection" is a question people actually ask of a call log. */
    from: typeof search.from === "string" && search.from ? search.from : undefined,
    to: typeof search.to === "string" && search.to ? search.to : undefined,
    /* The page lives in the URL with its filters. Tabs and search navigate without it (new list on
     * page one); previous/next keep it, so pages are shareable and Back works. */
    offset:
      typeof search.offset === "number" && Number.isInteger(search.offset) && search.offset > 0
        ? search.offset
        : undefined,
  }),
  component: CallLog,
});

/** The `from` a window ending on `today` means, as a date the API accepts. */
const windowStart = (today: string, days: number) => shiftDay(today, -(days - 1));

const TAB_LABELS: Record<CallTab, string> = {
  all: "All",
  attention: "Needs attention",
  handled: "Handled",
  messages: "Messages",
  transfers: "Transfers",
  robocalls: "Robocalls",
};

const PAGE = 50;

/** The tab and every filter back to "any"; the search box is its own to clear. */
const UNFILTERED = {
  tab: undefined,
  assignee: undefined,
  residentId: undefined,
  transferredTo: undefined,
  days: undefined,
  from: undefined,
  to: undefined,
};

/** Every call the building took, and what still needs somebody. */
function CallLog() {
  const { facilityId } = useFacility();
  const today = useFacilityToday(facilityId);
  const canManage = useCan("frontdesk:manage");
  const canTriage = useCan("frontdesk:call");
  const search = Route.useSearch();
  const {
    tab,
    q,
    assignee,
    residentId,
    transferredTo,
    days,
    from,
    to,
    offset: pageOffset,
  } = search;
  const navigate = Route.useNavigate();
  const offset = pageOffset ?? 0;
  const [pageSize, setPageSize] = useState(PAGE);
  const setOffset = (next: number) =>
    navigate({ search: { ...search, offset: next > 0 ? next : undefined }, replace: true });
  /* Every filter change opens page one. Merged rather than listed, so adding a
     filter does not mean remembering to thread it through five call sites,
     which is how the last one silently cleared the others. */
  const refine = (part: Partial<typeof search>) =>
    navigate({ search: { ...search, ...part, offset: undefined } });

  const searchText = useSearchParamText(q, (next) =>
    navigate({
      search: { ...search, q: next, offset: undefined },
      ...KEEP_PLACE,
    }),
  );
  const calls = useQuery({
    ...orpc.frontDesk.calls.list.queryOptions({
      input: {
        facilityId,
        tab,
        search: q,
        assignee,
        residentId,
        transferredTo,
        /* A typed range wins over a preset: the preset is what the range box
           is empty of. */
        ...(from ? { from } : days ? { from: windowStart(today, days) } : {}),
        ...(to ? { to } : {}),
        limit: pageSize,
        offset,
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const total = calls.data?.total ?? 0;

  /* Who can be GIVEN work, which is not the same list as who already holds something: a building
   * where nobody has taken anything still has people. Only when one building is chosen: a roll of
   * every person across every building is not a list anybody can pick a name out of. */
  const assignable = useQuery({
    ...orpc.frontDesk.calls.followUpPeople.queryOptions({ input: { facilityId } }),
    enabled: facilityId !== "all",
  });
  const columns = callColumns({ people: assignable.data ?? [], canManage });
  const filtered = Boolean(tab || assignee || residentId || transferredTo || days || from || to);

  return (
    <>
      <PageHeader
        title="Front desk calls"
        count={calls.data ? total : undefined}
        meta={
          /* The building chosen in the switcher, not the first row's: with
             every building chosen, the first row's name read as a filter. */
          calls.data && facilityId !== "all"
            ? `Every call ${calls.data.rows[0]?.facilityName ?? "this building"} took, and what still needs somebody.`
            : "Every call the front desk took, and what still needs somebody."
        }
      />

      {/* Search, chips, then the folded filters: the toolbar every list has. */}
      <ListToolbar
        search={
          <SearchField
            placeholder="Search what was said"
            value={searchText.text}
            className="sm:w-full"
            onChange={searchText.change}
          />
        }
        chips={
          /* One line that scrolls on a phone rather than two that wrap. The
             number is the reason to look at a tab at all. */
          <div className="flex max-w-full gap-1.5 overflow-x-auto max-sm:pb-1">
            {CALL_TABS.map((option) => (
              <FilterChip
                key={option}
                selected={(tab ?? "all") === option}
                count={calls.data ? calls.data.counts[option] : undefined}
                onClick={() => refine({ tab: option === "all" ? undefined : option })}
              >
                {TAB_LABELS[option]}
              </FilterChip>
            ))}
          </div>
        }
        trailing={
          <CallFilters
            values={{ assignee, residentId, transferredTo, days, from, to }}
            refine={refine}
            people={calls.data?.assignees ?? []}
            residents={calls.data?.residents ?? []}
            targets={calls.data?.transferTargets ?? []}
            unassigned={calls.data ? calls.data.unassigned : null}
          />
        }
      />

      {calls.isError ? (
        <TableCard>
          <ReadFailed
            message="Couldn't load the calls."
            error={calls.error}
            onRetry={() => void calls.refetch()}
            retrying={calls.isFetching}
          />
        </TableCard>
      ) : (
        <DataTable
          data={calls.data?.rows ?? []}
          columns={columns}
          noun="calls"
          rowId={(call) => call.id}
          busy={calls.isPlaceholderData}
          pending={calls.isPending || (calls.isPlaceholderData && !calls.data?.rows.length)}
          /* Production holds thousands of calls: without the pages the rest were not reachable. */
          server={{
            rowCount: total,
            pagination: { pageIndex: Math.floor(offset / pageSize), pageSize },
            onPaginationChange: (next) => {
              setPageSize(next.pageSize);
              setOffset(next.pageIndex * next.pageSize);
            },
          }}
          renderRowActions={(call) =>
            callHasActions(call, canTriage) ? <CallRowActions call={call} /> : null
          }
          empty={
            q ? (
              <EmptyState
                title={`No calls mention "${q}".`}
                description="Try other words, or clear the search box."
              />
            ) : filtered ? (
              /* Filtered to nothing is not "no calls": say so, and undo it in one press. */
              <EmptyState
                title="No calls match these filters."
                action={
                  <Button variant="outline" size="sm" onClick={() => refine(UNFILTERED)}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No calls yet."
                description="Calls the front desk takes appear here, each with Sarah's summary. Ring it from this computer to hear what a caller hears."
                action={
                  <Button asChild variant="outline" size="sm">
                    <Link to="/sarah/agent-test">Run a test call</Link>
                  </Button>
                }
              />
            )
          }
        />
      )}
    </>
  );
}
